import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Toaster } from "@/components/ui/sonner";
import { AIChatBubble } from "@/components/AIAssistant";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { ScrollToTop } from "@/components/ScrollToTop";
import { EmergencyButton } from "@/components/EmergencyButton";
import { CountryProvider } from "@/contexts/CountryContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { I18nProvider } from "@/contexts/I18nContext";

// Code-split every page — keeps the initial bundle small and the site fast.
const HomePage = lazy(() => import("@/pages/HomePage"));
const CountryPage = lazy(() => import("@/pages/CountryPage"));
const CityPage = lazy(() => import("@/pages/CityPage"));
const SearchPage = lazy(() => import("@/pages/SearchPage"));
const CategoriesPage = lazy(() => import("@/pages/CategoriesPage"));
const ServicesPage = lazy(() => import("@/pages/ServicesPage"));
const TransitPage = lazy(() => import("@/pages/TransitPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const AuthCallbackPage = lazy(() => import("@/pages/AuthCallbackPage"));
const ClaimBusinessPage = lazy(() => import("@/pages/ClaimBusinessPage"));
const BusinessDetailPage = lazy(() => import("@/pages/BusinessDetailPage"));
const AIPage = lazy(() => import("@/pages/AIPage"));
const AdminDashboardPage = lazy(() => import("@/pages/AdminDashboardPage"));
const PlanMyTripPage = lazy(() => import("@/pages/PlanMyTripPage"));
const TravelStoryPage = lazy(() => import("@/pages/TravelStoryPage"));
const StoriesListPage = lazy(() => import("@/pages/TravelStoryPage").then((m) => ({ default: m.StoriesListPage })));
const BrandHubPage = lazy(() => import("@/pages/BrandHubPage"));
const CityCategoryPage = lazy(() => import("@/pages/CityCategoryPage"));
const TouristEssentialsPage = lazy(() => import("@/pages/TouristEssentialsPage"));
const MapPage = lazy(() => import("@/pages/MapPage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const PaymentSuccessPage = lazy(() => import("@/pages/PaymentSuccessPage"));
const CategoryHubPage = lazy(() => import("@/pages/CategoryHubPage"));
const CategoryCityPage = lazy(() => import("@/pages/CategoryCityPage"));
const ListingDetailPage = lazy(() => import("@/pages/ListingDetailPage"));
const VisitCityPage = lazy(() => import("@/pages/VisitCityPage"));
const FavoritesPage = lazy(() => import("@/pages/FavoritesPage"));
const MicroSEOLanding = lazy(() => import("@/pages/MicroSEOLanding"));
const MoodHubPage = lazy(() => import("@/pages/MoodHubPage"));
const RadarPage = lazy(() => import("@/pages/RadarPage"));
const BusinessDashboardPage = lazy(() => import("@/pages/BusinessDashboardPage"));
const PassportPage = lazy(() => import("@/pages/PassportPage"));
const DnaQuizPage = lazy(() => import("@/pages/DnaQuizPage"));
const RecommendationsPage = lazy(() => import("@/pages/RecommendationsPage"));
const LeaderboardPage = lazy(() => import("@/pages/LeaderboardPage"));
const PublicPassportPage = lazy(() => import("@/pages/PublicPassportPage"));
const ItineraryPage = lazy(() => import("@/pages/ItineraryPage"));
const AskLocalPage = lazy(() => import("@/pages/AskLocalPage"));
const QuestionPage = lazy(() => import("@/pages/QuestionPage"));
const EventsPage = lazy(() => import("@/pages/EventsPage"));
const EmergencyPage = lazy(() => import("@/pages/EmergencyPage"));
const EnvReportsPage = lazy(() => import("@/pages/EnvReportsPage"));
const ChallengesPage = lazy(() => import("@/pages/ChallengesPage"));

const PageLoader = () => (
  <div className="flex-1 flex items-center justify-center py-32" data-testid="page-loader">
    <div className="w-10 h-10 border-4 border-emerald-200 border-t-[#00AA6C] rounded-full animate-spin" />
  </div>
);

const Chrome = ({ children }) => {
  const { pathname } = useLocation();
  const isPanel = pathname.startsWith('/admin') || pathname.startsWith('/dashboard');
  return (
    <>
      <Navbar />
      {children}
      <Footer />
      {!isPanel && <AIChatBubble />}
      {!isPanel && <EmergencyButton />}
      {!isPanel && <WhatsAppButton />}
    </>
  );
};

function App() {
  return (
    <HelmetProvider>
      <div className="flex min-h-screen flex-col bg-gray-50">
        <BrowserRouter>
          <ScrollToTop />
          <AuthProvider>
            <I18nProvider>
              <CountryProvider>
                <Chrome>
                <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/ai" element={<AIPage />} />
                  <Route path="/plan-trip" element={<PlanMyTripPage />} />
                  <Route path="/stories" element={<StoriesListPage />} />
                  <Route path="/stories/:slug" element={<TravelStoryPage />} />
                  <Route path="/guides" element={<StoriesListPage />} />
                  <Route path="/guides/:slug" element={<TravelStoryPage />} />
                  <Route path="/map" element={<MapPage />} />
                  <Route path="/radar" element={<RadarPage />} />
                  <Route path="/pricing" element={<PricingPage />} />
                  <Route path="/payment-success" element={<PaymentSuccessPage />} />
                  <Route path="/search" element={<SearchPage />} />
                  <Route path="/categories" element={<CategoriesPage />} />
                  <Route path="/services" element={<ServicesPage />} />
                  <Route path="/transit" element={<TransitPage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/login" element={<LoginPage mode="login" />} />
                  <Route path="/register" element={<LoginPage mode="register" />} />
                  <Route path="/auth/callback" element={<AuthCallbackPage />} />
                  <Route path="/favorites" element={<FavoritesPage />} />
                  <Route path="/passport" element={<PassportPage />} />
                  <Route path="/dna-quiz" element={<DnaQuizPage />} />
                  <Route path="/recommendations" element={<RecommendationsPage />} />
                  <Route path="/leaderboard" element={<LeaderboardPage />} />
                  <Route path="/u/:userId" element={<PublicPassportPage />} />
                  <Route path="/itinerary/:id" element={<ItineraryPage />} />
                  <Route path="/ask" element={<AskLocalPage />} />
                  <Route path="/ask/q/:qid" element={<QuestionPage />} />
                  <Route path="/emergency" element={<EmergencyPage />} />
                  <Route path="/reports" element={<EnvReportsPage />} />
                  <Route path="/challenges" element={<ChallengesPage />} />
                  <Route path="/dashboard" element={<BusinessDashboardPage />} />
                  <Route path="/dashboard/:tab" element={<BusinessDashboardPage />} />
                  <Route path="/admin" element={<AdminDashboardPage />} />
                  <Route path="/claim" element={<ClaimBusinessPage />} />
                  <Route path="/business/:id" element={<BusinessDetailPage />} />
                  <Route path="/business/:brandSlug/:locationSlug" element={<BusinessDetailPage />} />
                  <Route path="/brand/:slug" element={<BrandHubPage />} />

                  {/* SPRINT 1 — Marketplace + programmatic SEO routes */}
                  {/* Category hubs */}
                  <Route path="/hotels" element={<CategoryHubPage type="hotel" />} />
                  <Route path="/restaurants" element={<CategoryHubPage type="restaurant" />} />
                  <Route path="/cafes" element={<CategoryHubPage type="cafe" />} />
                  <Route path="/bars" element={<CategoryHubPage type="bar" />} />
                  <Route path="/beaches" element={<CategoryHubPage type="beach" />} />
                  <Route path="/attractions" element={<CategoryHubPage type="attraction" />} />
                  <Route path="/tours" element={<CategoryHubPage type="tour" />} />
                  <Route path="/events" element={<EventsPage />} />
                  <Route path="/events-hub" element={<CategoryHubPage type="event" />} />
                  {/* Category × city */}
                  <Route path="/hotels/:city" element={<CategoryCityPage type="hotel" />} />
                  <Route path="/restaurants/:city" element={<CategoryCityPage type="restaurant" />} />
                  <Route path="/cafes/:city" element={<CategoryCityPage type="cafe" />} />
                  <Route path="/bars/:city" element={<CategoryCityPage type="bar" />} />
                  <Route path="/beaches/:city" element={<CategoryCityPage type="beach" />} />
                  <Route path="/attractions/:city" element={<CategoryCityPage type="attraction" />} />
                  <Route path="/tours/:city" element={<CategoryCityPage type="tour" />} />
                  <Route path="/events/:city" element={<CategoryCityPage type="event" />} />
                  {/* Listing detail (singular paths) */}
                  <Route path="/hotel/:slug" element={<ListingDetailPage type="hotel" />} />
                  <Route path="/restaurant/:slug" element={<ListingDetailPage type="restaurant" />} />
                  <Route path="/cafe/:slug" element={<ListingDetailPage type="cafe" />} />
                  <Route path="/bar/:slug" element={<ListingDetailPage type="bar" />} />
                  <Route path="/beach/:slug" element={<ListingDetailPage type="beach" />} />
                  <Route path="/attraction/:slug" element={<ListingDetailPage type="attraction" />} />
                  <Route path="/tour/:slug" element={<ListingDetailPage type="tour" />} />
                  {/* Visit-city landing pages */}
                  <Route path="/visit-:city" element={<VisitCityPage />} />
                  {/* Mood hubs */}
                  <Route path="/mood/:mood" element={<MoodHubPage />} />
                  {/* Micro-SEO long-tail pages */}
                  <Route path="/best-:typePlural-in-:city" element={<MicroSEOLanding intent="best" />} />
                  <Route path="/romantic-:typePlural-in-:city" element={<MicroSEOLanding intent="romantic" />} />
                  <Route path="/family-:typePlural-in-:city" element={<MicroSEOLanding intent="family" />} />
                  <Route path="/halal-:typePlural-in-:city" element={<MicroSEOLanding intent="halal" />} />
                  <Route path="/luxury-:typePlural-in-:city" element={<MicroSEOLanding intent="luxury" />} />
                  <Route path="/cheap-:typePlural-in-:city" element={<MicroSEOLanding intent="cheap" />} />
                  <Route path="/weekend-trips-from-:city" element={<MicroSEOLanding intent="weekend" />} />

                  {/* Country / city legacy URLs (kept) */}
                  <Route path="/:country" element={<CountryPage />} />
                  <Route path="/:country/:city" element={<CityPage />} />
                  <Route path="/:country/:city/:category" element={<CityCategoryPage />} />
                  <Route path="/essentials/:country/:city" element={<TouristEssentialsPage />} />
                </Routes>
                </Suspense>
              </Chrome>
              <Toaster position="top-center" />
              </CountryProvider>
            </I18nProvider>
          </AuthProvider>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
